import React, { useCallback, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@material-ui/core';
import LoadingState from 'components/kea/LoadingState';
import WidgetPreview from '../../../../search-experience-v2/preview/WidgetPreview';
import CanvasResolver from '../../../../search-experience-v2/core/renderer/CanvasResolver';
import ComponentSettingsPanel from './ComponentSettingsPanel';
import { useSearchExperience } from '../../../../search-experience-v2/api/useSearchExperience';
import ComponentRegistry from '../../../../search-experience-v2/core/registry/ComponentRegistry';
import IntlMessages from 'util/IntlMessages';

const REQUIRED_VIEW_SCREENS = {
  searchBox: ['empty', 'results', 'noResults'],
  dropdown: ['empty', 'results', 'noResults'],
  resultsPage: ['results', 'noResults'],
};

const EDITOR_CONFIGURABLE_TYPES = new Set([
  'products',
  'facets',
  'suggestions',
  'catSuggestions',
  'history',
  'pills',
  'sort',
  'filtersButton',
  'popularQueries',
  'didYouMean',
  'pagination',
  'noResults',
  'viewAll',
]);

const hasConfigurableNodes = (nodes) => {
  if (!Array.isArray(nodes)) return false;

  return nodes.some((node) => (
    EDITOR_CONFIGURABLE_TYPES.has(node?.type)
    || hasConfigurableNodes(node?.nodes)
  ));
};

const hasEditorCanvas = (canvas) => Boolean(
  canvas
  && typeof canvas === 'object'
  && hasConfigurableNodes(canvas.nodes),
);

const hasRequiredLayouts = (config, view) => {
  const viewConfig = config?.search?.views?.[view];
  if (!viewConfig) return false;

  const requiredScreens = REQUIRED_VIEW_SCREENS[view] || ['results'];
  return requiredScreens.every((screen) => (
    hasEditorCanvas(viewConfig.screens?.[screen]?.canvas)
  ));
};

/**
 * WidgetEditor - Isolated editor for widget configurations (SearchBox, Dropdown, ResultsPage)
 *
 * Uses the full search-experience-v2 infrastructure:
 * - useSearchExperience hook (loading, saving, validation, error handling)
 * - WidgetPreview for rendering
 * - ComponentSettingsPanel for editing
 *
 * This is a thin wrapper that provides:
 * 1. Integration with wizard flow (onReady callback)
 * 2. Scoped view/screen/device selection
 * 3. Convenient settingsPanel + previewPanel interface
 */
const WidgetEditor = ({
  shopId,
  client,
  view = 'searchBox',
  experienceType = null,
  onReady,
  onConfigChange,
}) => {
  const [previewPresentation, setPreviewPresentation] = React.useState(undefined);
  const presentation = view === 'dropdown' ? previewPresentation : undefined;
  // Use the full search-experience-v2 hook
  const {
    config,
    loading,
    isLoading,
    isLoadingRegistry,
    error,
    saving,
    validationErrors,
    currentScreen,
    currentDevice,
    loadConfig,
    loadRegistry,
    saveConfig,
    update,
    changeScreen,
    changeDevice,
    validateNow,
  } = useSearchExperience(client, shopId);
  const hasConfig = !!config;
  const configExperience = config?.search?.searchExperience || null;
  const needsConfigReload = !hasConfig
    || (experienceType && configExperience !== experienceType)
    || !hasRequiredLayouts(config, view);
  const loadAttemptKey = `${shopId || 'no-shop'}:${experienceType || 'any'}:${view}`;
  const loadAttemptKeyRef = React.useRef(null);
  const shouldReloadConfig = needsConfigReload && loadAttemptKeyRef.current !== loadAttemptKey;
  const isConfigLoading = Boolean(loading || isLoading || isLoadingRegistry);

  // Load configuration on mount
  useEffect(() => {
    let isMounted = true;

    const init = async () => {
      try {
        if (!ComponentRegistry.isLoaded()) {
          await loadRegistry();
        }

        if (shouldReloadConfig) {
          loadAttemptKeyRef.current = loadAttemptKey;
          await loadConfig('preview');
        }

        if (isMounted && onReady) {
          onReady(true);
        }
      } catch (err) {
        if (isMounted && onReady) {
          onReady(false);
        }
      }
    };

    init();

    return () => {
      isMounted = false;
    };
  }, [shopId, loadConfig, loadRegistry, onReady, shouldReloadConfig, loadAttemptKey]);

  const handleSettingsChange = useCallback((nextConfig) => {
    update(nextConfig);
    if (onConfigChange) {
      onConfigChange(nextConfig);
    }
  }, [onConfigChange, update]);

  // Resolve canvas for preview
  const previewCanvas = useMemo(() => {
    if (!config) return null;
    return CanvasResolver.resolve(config, {
      view,
      screen: currentScreen,
      device: currentDevice,
      visualSearch: false,
      presentation,
    });
  }, [config, view, currentScreen, currentDevice, presentation]);

  // Loading state
  if (isConfigLoading || shouldReloadConfig) {
    return <LoadingState variant="panel" />;
  }

  // Error state
  if (error) {
    return (
      <Box p={3}>
        <Typography color="error" variant="h6" gutterBottom>
          <IntlMessages id="ui.embedded.editors.widgetEditor.widgetEditor.failedToLoadConfiguration" />
        </Typography>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  // No config state
  if (!config) {
    return (
      <Box p={3}>
        <Typography><IntlMessages id="ui.embedded.editors.widgetEditor.widgetEditor.noConfigurationAvailable" /></Typography>
      </Box>
    );
  }

  // Return editor interface
  return {
    settingsPanel: (
      <ComponentSettingsPanel
        config={config}
        view={view}
        screen={currentScreen}
        device={currentDevice}
        presentation={presentation}
        onChange={handleSettingsChange}
      />
    ),
    previewPanel: (
      <WidgetPreview
        canvas={previewCanvas}
        viewConfig={config.search?.views?.[view]}
        onPresentationChange={view === 'dropdown' ? setPreviewPresentation : undefined}
        theme={config.theme}
        viewType={view}
        screen={currentScreen}
        device={currentDevice}
        mode="text"
      />
    ),
    controls: {
      currentScreen,
      currentDevice,
      setCurrentScreen: changeScreen,
      setCurrentDevice: changeDevice,
      saveConfig,
      reloadConfig: loadConfig,
      validateConfig: validateNow,
    },
    state: {
      config,
      saving,
      validationErrors,
    },
  };
};

WidgetEditor.propTypes = {
  shopId: PropTypes.string.isRequired,
  client: PropTypes.shape({
    loadSearchExperienceConfig: PropTypes.func,
    saveSearchExperienceConfig: PropTypes.func,
  }).isRequired,
  view: PropTypes.string,
  experienceType: PropTypes.string,
  onReady: PropTypes.func,
  onConfigChange: PropTypes.func,
};

WidgetEditor.defaultProps = {
  view: 'searchBox',
  experienceType: null,
  onReady: null,
  onConfigChange: null,
};

export default WidgetEditor;
